import { useEffect, useState } from "react";
import { Landmark } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectPortalMandate } from "../../features/portal/portal-slice";
import {
  createPortalMandateSetupSession,
  fetchPortalMandate,
} from "../../features/portal/slice-extensions/portal-slice-extension";
import { DebitMandateStatus } from "../../features/collection/models/DebitMandateStatus";
import { PORTAL_MANDATE_ANCHOR } from "./PortalPaymentsList";

/**
 * The member's automatic-payment standing in the portal (SEPA mandate or card on file,
 * PD-0043): an Active mandate shows its label ("Visa ···4242 · active"); otherwise (when
 * the organization accepts mandates) a "Set up automatic payments" button hands off to
 * Stripe's hosted page.
 */
const PortalMandateCard = ({ memberId }: { memberId: string }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const mandate = useAppSelector(selectPortalMandate(memberId));
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchPortalMandate(memberId));
  }, [dispatch, memberId]);

  if (!mandate || (!mandate.setupAvailable && mandate.status !== DebitMandateStatus.Active)) {
    return null;
  }

  const handleSetup = async () => {
    setError(null);
    setRedirecting(true);
    try {
      const url = await dispatch(createPortalMandateSetupSession(memberId)).unwrap();
      window.location.assign(url);
    } catch (e) {
      setError((e as { message?: string })?.message ?? t("portal.mandate.error", "Setting up automatic payments is unavailable right now."));
      setRedirecting(false);
    }
  };

  return (
    <div id={PORTAL_MANDATE_ANCHOR} className="with-border mt-4 rounded-2xl bg-white p-3 dark:bg-primary-20">
      <p className="flex items-center gap-2 font-medium text-grey-20 dark:text-white">
        <Landmark size={16} className="shrink-0 text-grey-30 dark:text-grey-70" />
        {t("portal.mandate.title", "Automatic payments")}
      </p>
      {mandate.status === DebitMandateStatus.Active ? (
        <p className="mt-1 text-sm text-grey-40 dark:text-grey-80">
          {mandate.label
            ? t("portal.mandate.activeLabel", "{{label}} · active. Your payments are collected automatically.", {
                label: mandate.label,
              })
            : t("portal.mandate.active", "Payments are collected automatically from your bank account{{last4}}.", {
                last4: mandate.bankLast4 ? ` ····${mandate.bankLast4}` : "",
              })}
        </p>
      ) : (
        <div className="mt-1">
          <p className="text-sm text-grey-40 dark:text-grey-80">
            {t("portal.mandate.intro", "Set up automatic payments once and your fees are collected for you.")}
          </p>
          {error && <p className="mt-1 text-sm font-medium text-red-700 dark:text-red-400">{error}</p>}
          <button
            type="button"
            className="btn btn-secondary mt-2"
            disabled={redirecting}
            onClick={handleSetup}
          >
            {t("portal.mandate.setup", "Set up automatic payments")}
          </button>
        </div>
      )}
    </div>
  );
};

export default PortalMandateCard;
